/**
 * Edit one platform's profile copy in the shared Drawer (DD-051). The host
 * passes the fields it allows and saves the draft its own way: Home through
 * the admin client update, the portal through its own endpoint. Character
 * limits warn (amber) but never block; non-http(s) URLs block save.
 */

import React, { useState } from "react";
import { Drawer } from "@/components/ui/Drawer";
import { Button } from "@/components/ui/Button";
import { SocialPlatform, SocialProfile, SocialProfileField } from "@/types";
import type { Locale } from "@/hooks/useLocale";
import {
  PROFILE_COPY,
  fieldHint,
  fieldLabel,
  isOverLimit,
  isUrlField,
  isValidProfileUrl,
  limitFor,
} from "../socialProfiles";

interface SocialProfileDrawerProps {
  isOpen: boolean;
  platform: SocialPlatform;
  profile: SocialProfile;
  fields: SocialProfileField[];
  locale?: Locale;
  /** Shown in an empty website field, e.g. the client's site from Settings. */
  websitePlaceholder?: string;
  /** Rejects to keep the drawer open with the error shown. */
  onSave: (profile: SocialProfile) => Promise<void>;
  onClose: () => void;
}

const INPUT_CLASS =
  "w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-hidden transition-colors";

export const SocialProfileDrawer: React.FC<SocialProfileDrawerProps> = ({
  isOpen,
  platform,
  profile,
  fields,
  locale = "en",
  websitePlaceholder,
  onSave,
  onClose,
}) => {
  const copy = PROFILE_COPY[locale];
  const [draft, setDraft] = useState<SocialProfile>({});
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [wasOpen, setWasOpen] = useState(false);

  // Seed the draft once per open, not on every new `profile` identity: a
  // refetch while the drawer is open must not wipe what is being typed.
  if (isOpen !== wasOpen) {
    setWasOpen(isOpen);
    if (isOpen) {
      setDraft(profile);
      setError(null);
    }
  }

  const invalidUrl = fields.some((f) => isUrlField(f) && !isValidProfileUrl(draft[f] ?? ""));

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    try {
      await onSave(draft);
    } catch (err) {
      setError(err instanceof Error ? err.message : copy.saveFailed);
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    if (!isSaving) onClose();
  };

  const header = (
    <h2 className="text-lg font-semibold text-gray-900">{copy.drawerTitle(platform)}</h2>
  );

  // No Cancel: the X closes, and handleClose holds it while saving (J12).
  const footer = (
    <Button type="button" onClick={handleSave} disabled={isSaving || invalidUrl}>
      {isSaving ? copy.saving : copy.save}
    </Button>
  );

  return (
    <Drawer
      isOpen={isOpen}
      onClose={handleClose}
      header={header}
      footer={footer}
      bodyPadding
      a11yTitle={copy.drawerTitle(platform)}
    >
      <div className="space-y-5">
        {fields.map((field) => {
          const value = draft[field] ?? "";
          const limit = limitFor(platform, field);
          const hint = fieldHint(platform, field, locale);
          const over = isOverLimit(value, limit);
          const badUrl = isUrlField(field) && !isValidProfileUrl(value);
          const placeholder = field === "website" ? websitePlaceholder : undefined;
          const onChange = (
            e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
          ) => setDraft({ ...draft, [field]: e.target.value });

          return (
            <div key={field}>
              <div className="flex items-baseline justify-between mb-1">
                <label className="block text-sm font-medium text-gray-700">
                  {fieldLabel(platform, field, locale)}
                </label>
                {limit !== null && (
                  <span className={`text-xs ${over ? "text-amber-600" : "text-gray-400"}`}>
                    {value.length} / {limit}
                  </span>
                )}
              </div>
              {field === "bio" ? (
                <textarea
                  value={value}
                  onChange={onChange}
                  rows={platform === "google" ? 6 : 3}
                  className={INPUT_CLASS}
                  disabled={isSaving}
                />
              ) : (
                <input
                  type={isUrlField(field) ? "url" : "text"}
                  value={value}
                  onChange={onChange}
                  placeholder={placeholder}
                  className={INPUT_CLASS}
                  disabled={isSaving}
                />
              )}
              {hint && <p className="mt-1 text-xs text-gray-500">{hint}</p>}
              {badUrl && <p className="mt-1 text-xs text-red-600">{copy.badUrl}</p>}
            </div>
          );
        })}
        {error && <p className="text-sm text-red-600">{error}</p>}
      </div>
    </Drawer>
  );
};
